import { useCallback, useEffect, useMemo, useState } from 'react';

import {
    SCHEMA_1_CONSTANTS,
    SCHEMA_2_CONSTANTS,
    SCHEMA_3_CONSTANTS,
} from '../../registration/components/registration-flow/registration.constants';
import type { IStep } from '../components/form-flow/form-flow';

import { usePrevious } from './use-previous';

export interface IUseStepFlowProps {
    steps: ReadonlyArray<Readonly<IStep>>;
    formDataProps?: Record<string, unknown>;
}

export function useStepFlow({ steps, formDataProps }: IUseStepFlowProps) {
    const [currentStep, setCurrentStep] = useState(steps[0].name);
    const [stepAdditionalData, setStepData] = useState<Record<string, unknown>>({});
    const [stepDataOverrides, setStepDataOverrides] = useState<Record<string, unknown>>({});
    const [isLoadingNextStepData, setIsLoadingNextStepData] = useState(true);
    const [isWaitingForSubmission, setIsWaitingForSubmission] = useState(false);
    const [errors, setErrors] = useState<
        Array<{ type: 'field' | 'form'; fieldName?: string; messages?: string[] }> | undefined
    >();

    const currentStepDefinition = useMemo(() => {
        return steps.find((item) => item.name === currentStep);
    }, [currentStep, steps]);
    const [previousStepDefinition, setPreviousStep] = useState<IStep>();
    const step = usePrevious(currentStepDefinition);

    const loadStepData = useCallback(
        async (stepDefinition?: IStep) => {
            if (!stepDefinition || !stepDefinition.onBeforeEnterStep) {
                setIsLoadingNextStepData(false);
                setStepData({});
                return;
            }

            setIsLoadingNextStepData(true);

            const data = await stepDefinition.onBeforeEnterStep(formDataProps);
            setStepData(data);
            setIsLoadingNextStepData(false);
        },
        [setIsLoadingNextStepData, setStepData, formDataProps],
    );

    useEffect(() => {
        if (step?.name === currentStepDefinition?.name) {
            return;
        }

        loadStepData(currentStepDefinition);

        setIsWaitingForSubmission(false);
    }, [loadStepData, currentStepDefinition, step]);

    const goToStep = useCallback(
        (stepName: string) => {
            const currentStepIndex = steps.findIndex((step) => {
                return step.name === currentStepDefinition?.name;
            });

            setPreviousStep(steps[currentStepIndex - 1]);
            setCurrentStep(stepName);
        },
        [steps, currentStepDefinition?.name],
    );

    const goToNextStep = useCallback(
        (currentFormData: Record<string, unknown>) => {
            const currentStepIndex = steps.findIndex((step) => {
                return step.name === currentStepDefinition?.name;
            });

            const moveStep = (stepDataOverrides?: Record<string, unknown>) => {
                const nextStep = steps[currentStepIndex + 1];
                setPreviousStep(steps[currentStepIndex]);
                setCurrentStep(nextStep?.name);
                setStepDataOverrides(stepDataOverrides || {});
            };

            return {
                isLastStep: currentStepIndex + 1 === steps.length,
                onLeave: (async () => {
                    if (!currentStepDefinition) {
                        moveStep();
                        return true;
                    }

                    setIsWaitingForSubmission(true);

                    setErrors(undefined);
                    const response = await currentStepDefinition.onBeforeLeaveStep?.(
                        currentFormData,
                        {
                            goToStep: (stepName: string) => {
                                setPreviousStep(steps[currentStepIndex - 1]);
                                setCurrentStep(stepName);
                            },
                        },
                    );

                    const additionalData = response?.additionalData;
                    const errors = response?.errors;

                    setIsWaitingForSubmission(false);
                    if (errors) {
                        setErrors(errors);
                        errors.map((error) => {
                            if (
                                error.fieldName &&
                                Object.values(SCHEMA_1_CONSTANTS).includes(error?.fieldName)
                            ) {
                                setCurrentStep('step-1');
                            } else if (
                                error.fieldName &&
                                Object.values(SCHEMA_2_CONSTANTS).includes(error?.fieldName)
                            ) {
                                setCurrentStep('step-2');
                            } else if (
                                error.fieldName &&
                                Object.values(SCHEMA_3_CONSTANTS).includes(error?.fieldName)
                            ) {
                                setCurrentStep('step-3');
                            } else {
                                setCurrentStep('step-1');
                            }
                        });
                        return false;
                    }

                    moveStep(additionalData);
                    return true;
                })(),
            };
        },
        [setPreviousStep, currentStepDefinition, setStepDataOverrides, setCurrentStep, steps],
    );

    const displayedStep = useMemo(
        () => (isLoadingNextStepData ? previousStepDefinition : currentStepDefinition),
        [isLoadingNextStepData, currentStepDefinition, previousStepDefinition],
    );

    return useMemo(
        () => ({
            isLoading: isLoadingNextStepData || isWaitingForSubmission,
            stepAdditionalData: {
                ...stepAdditionalData,
                ...stepDataOverrides,
            },
            currentStep: currentStepDefinition,
            previousStep: previousStepDefinition,
            displayedStep,
            errors,
            goToNextStep,
            goToStep,
            setCurrentStep,
        }),
        [
            isLoadingNextStepData,
            isWaitingForSubmission,
            stepAdditionalData,
            stepDataOverrides,
            currentStepDefinition,
            previousStepDefinition,
            displayedStep,
            errors,
            goToNextStep,
            setCurrentStep,
        ],
    );
}
